import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { adminApi } from "../../../api/adminApi";

const ACCION_CONFIG = {
  "cambio-rol":          { label: "Cambio de rol",        severity: "warning" },
  "editar-rol":          { label: "Editar permisos",       severity: "info" },
  "crear-rol":           { label: "Crear rol",             severity: "success" },
  "eliminar-rol":        { label: "Eliminar rol",          severity: "danger" },
  "editar-notificaciones": { label: "Notificaciones",      severity: "secondary" },
};

function formatFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function detalleTexto(entry) {
  const d = entry.detalle || {};
  switch (entry.accion) {
    case "cambio-rol":
      return `${d.usuarioAfectado?.email || d.usuarioAfectado?.uid || "?"}: "${d.rolAnterior}" → "${d.rolNuevo}"`;
    case "editar-rol":
      return `Rol "${d.rolId}" — ${d.permisosNuevos?.length ?? "?"} permisos`;
    case "crear-rol":
      return `Nuevo rol "${d.rolId}" (${d.nombre})`;
    case "eliminar-rol":
      return `Rol "${d.rolId}" eliminado`;
    case "editar-notificaciones":
      return `"${d.tipo}": ${d.destinatariosNuevos?.length ?? "?"} destinatarios`;
    default:
      return JSON.stringify(d);
  }
}

export default function PanelAuditLog() {
  const toast = useRef(null);
  const [entries, setEntries] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [hayMas, setHayMas] = useState(false);
  const [lastId, setLastId] = useState(null);

  useEffect(() => {
    cargar();
  }, []);

  async function cargar(startAfter = null) {
    setCargando(true);
    try {
      const res = await adminApi.getAuditLog(50, startAfter);
      const nuevas = res.data.entries || [];
      setEntries((prev) => startAfter ? [...prev, ...nuevas] : nuevas);
      setHayMas(nuevas.length === 50);
      setLastId(nuevas.length > 0 ? nuevas[nuevas.length - 1].id : null);
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: "No se pudo cargar el historial" });
    } finally {
      setCargando(false);
    }
  }

  const accionTemplate = (row) => {
    const cfg = ACCION_CONFIG[row.accion] || { label: row.accion, severity: "secondary" };
    return <Tag value={cfg.label} severity={cfg.severity} />;
  };

  const fechaTemplate = (row) => <span className="text-sm">{formatFecha(row.timestamp)}</span>;

  const realizadoPorTemplate = (row) => (
    <span className="text-sm">{row.realizadoPor?.email || row.realizadoPor?.uid || "—"}</span>
  );

  const detalleTemplate = (row) => (
    <span className="text-sm text-600">{detalleTexto(row)}</span>
  );

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <div className="flex align-items-center justify-content-between mb-3">
        <h2 className="m-0">Historial de cambios</h2>
        <Button
          icon="pi pi-refresh"
          text
          label="Actualizar"
          size="small"
          loading={cargando}
          onClick={() => cargar()}
        />
      </div>

      <DataTable
        value={entries}
        loading={cargando}
        emptyMessage="Sin registros de auditoría"
        stripedRows
        size="small"
      >
        <Column header="Fecha" body={fechaTemplate} style={{ minWidth: "160px" }} />
        <Column header="Acción" body={accionTemplate} style={{ minWidth: "140px" }} />
        <Column header="Realizado por" body={realizadoPorTemplate} style={{ minWidth: "200px" }} />
        <Column header="Detalle" body={detalleTemplate} />
      </DataTable>

      {hayMas && (
        <div className="text-center mt-3">
          <Button
            label="Cargar más"
            text
            loading={cargando}
            onClick={() => cargar(lastId)}
          />
        </div>
      )}
    </div>
  );
}
